import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Copy, SlidersHorizontal, List } from 'lucide-react';
import type { FilterTemplateToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useFilterTemplatesList, useFilterTemplateMutations } from '~/hooks/use-filter-templates';
import { tokenToForm, type FilterTemplateFormValues } from '~/lib/schemas/filter-template';
import { FilterTemplateCopyDialog } from './copy-dialog';
import { FilterTemplateEditDialog } from './template-edit-dialog';
import { FilterTemplateValuesSheet } from './values-sheet';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Filter templates (Πρότυπα Φίλτρων) CRUD + per-template filter set — parity with the legacy grid + master-detail. */
export function FilterTemplatesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useFilterTemplatesList();
	const { remove } = useFilterTemplateMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<FilterTemplateFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<FilterTemplateToken | null>(null);
	const [filtersFor, setFiltersFor] = useState<FilterTemplateToken | null>(null);
	const [copySource, setCopySource] = useState<{ id: number; nm: string } | null>(null);

	const columns = useMemo<SystemGridColumn<FilterTemplateToken>[]>(
		() => [
			{
				key: 'name',
				header: t('catalog.filterTemplates.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'filters',
				header: '',
				width: '140px',
				render: o => (
					<Button
						variant="outline"
						size="sm"
						onClick={e => {
							e.stopPropagation();
							setFiltersFor(o);
						}}>
						<List size={14} /> {t('catalog.filterTemplates.filters')}
					</Button>
				)
			},
			{
				key: 'isDefault',
				header: t('catalog.filterTemplates.columns.isDefault'),
				width: '120px',
				align: 'center',
				sortValue: o => (o.IsDefault ? 1 : 0),
				exportValue: o => (o.IsDefault ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.IsDefault)} />
			},
			{
				key: 'isManufacturerDefault',
				header: t('catalog.filterTemplates.columns.isManufacturerDefault'),
				width: '150px',
				align: 'center',
				defaultVisible: false,
				sortValue: o => (o.IsManufacturerDefault ? 1 : 0),
				exportValue: o => (o.IsManufacturerDefault ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.IsManufacturerDefault)} />
			},
			{
				key: 'visible',
				header: t('catalog.filterTemplates.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => <Bool on={Boolean(o.iv)} />
			},
			{
				key: 'sort',
				header: t('catalog.filterTemplates.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<FilterTemplateToken>
				gridId="filter-templates"
				title={t('catalog.filterTemplates.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => (o.nm ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<SlidersHorizontal size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('catalog.filterTemplates.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				rowActions={o =>
					o.id != null ? (
						<Button
							variant="ghost"
							size="icon"
							aria-label={t('catalog.filterTemplates.copy')}
							title={t('catalog.filterTemplates.copy')}
							onClick={() => setCopySource({ id: o.id!, nm: o.nm ?? '' })}>
							<Copy size={15} />
						</Button>
					) : null
				}
				actionsWidth={136}
				exportFilename="filter-templates"
			/>

			<FilterTemplateEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<FilterTemplateCopyDialog source={copySource} onClose={() => setCopySource(null)} />

			<FilterTemplateValuesSheet
				open={Boolean(filtersFor)}
				onOpenChange={o => !o && setFiltersFor(null)}
				templateId={filtersFor?.id ?? null}
				templateName={filtersFor?.nm ?? ''}
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.filterTemplates.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.filterTemplates.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
